

import Vue from 'vue'
import Component from "vue-class-component";

import {Prop, Watch} from "vue-property-decorator";
import {namespace} from "vuex-class";
import {IOnOffHandlers} from "bonomo/dist";
import {EServiceProviderType, IServiceProvider} from "@/model/Service/serviceProviderInterfaces";
import {IErrors} from "@/model/IErrors";
import SPSettings from "@/components/serviceProvider/SPSettings.vue";
import SPSubscriptionSettings from "@/components/serviceProvider/SPSubscriptionSettings.vue";
import SPStripeDiscount from "@/components/serviceProvider/SPStripeDiscount.vue";
import SPLinkTracking from "@/components/serviceProvider/SPLinkTracking.vue";
import SPProfessionalServiceFields from "@/components/serviceProvider/SPProfessionalServiceFields.vue";
import SPAddress from "@/components/serviceProvider/SPAddress.vue";
import SPScope from "@/components/serviceProvider/SPScope.vue";
import SPInvoicing from "@/components/serviceProvider/SPInvoicing.vue";
import SPContact from "@/components/serviceProvider/SPContact.vue";
import SPTranslatableContent from "@/components/serviceProvider/SPTranslatableContent.vue";
import SPChannels from "@/components/serviceProvider/SPChannels.vue";
import SPCategories from "@/components/serviceProvider/SPCategories.vue";
import SPUsers from "@/components/serviceProvider/SPUsers.vue";
import ImageUploader from "@/components/shared/ImageUploader.vue";
import SPImage from "@/components/serviceProvider/SPImage.vue";
import {EPermissionActions} from "@/model/EPermissionActions";
import SPDuplicateDialog from "@/components/serviceProvider/SPDuplicateDialog.vue";
import LMRestrictCellDialog from "@/components/locker/modules/LMRestrictCellDialog.vue";
import V2SPImageInput from "@/components/serviceProvider/v2/SpImageInput/V2SPImageInput.vue";
import {getFileImage} from "@/helpers/fileHelpers";
import {ITranslatedField} from "@/helpers/TranslationHelper";
import SimpleAlertDialog from "@/components/shared/SimpleAlertDialog.vue";
import SPNav from "@/components/serviceProvider/widget/SPNav.vue";
import SPInfoCardInstructionList from "@/components/serviceProvider/SPInfoCardInstructionList.vue";
import {IServiceTag} from "@/model/Service/serviceInterfaces";


enum IMAGE_ENDPOINTS {
  logoImage = 'logoImage',
  brandImage = 'brandImage',
  secondImage = 'secondImage',
}

interface IProviderForm extends IServiceProvider {
  tag_ids?: (string| number)[]
}

const serviceProviderModule = namespace("serviceProviderModule");

  @Component({
    components: {
      SPNav,
      SimpleAlertDialog,
      V2SPImageInput,
      LMRestrictCellDialog,
      SPImage,
      ImageUploader,
      SPUsers,
      SPCategories,
      SPChannels,
      SPTranslatableContent,
      SPInfoCardInstructionList,
      SPContact,
      SPInvoicing,
      SPAddress,
      SPScope,
      SPSettings,
      SPSubscriptionSettings,
      SPStripeDiscount,
      SPLinkTracking,
      SPProfessionalServiceFields,
      SPDuplicateDialog
    }
  })
  export default class ServiceProvider extends Vue {

    @Prop()
    public spId!: number;

    @serviceProviderModule.Getter('serviceProvider')
    public provider!: IServiceProvider;
    @serviceProviderModule.Getter('errors')
    public errors!: IErrors;
    @serviceProviderModule.Action('fetchTags')
    protected fetchTags!: () => any;
    @serviceProviderModule.Getter('tags')
    protected availableTags!: IServiceTag[];
    public showDuplicationDialog: boolean = false;
    public showDeleteDialog: boolean = false;
    public deletingProvider: boolean = false;
    public showDeleteErrorDialog: boolean = false;
    public deleteErrorText: string = '';
    public showValidationDialog: boolean = false;

    public addressImageLogo: string|null = null
    public addressImageBrand: string|null = null
    public addressImageAnimation: string|null = null

    public fileImageLogo: File|null = null
    public fileImageBrand: File|null = null
    public fileImageAnimation: File|null = null
    public tagSearch: string = '';
    public tagIds: (IServiceTag | number | string)[] = [];

    @Watch('spId')
    spIdChanged() {
      this.resetFormData();
    }
    @Watch('isInfoCard')
    updateTagsIfInfoCard() {
      if (this.isInfoCard) {
        this.fetchTags(); // Tags could have changed since last time, specially if the provider has tags
      }
    }

    @Watch('provider')
    updateTags() {
      if (this.provider.tags && this.provider.tags.length > 0) {
        this.tagIds = [ ...this.provider.tags ];
      }
      if (this.isInfoCard) {
        this.fetchTags(); // Tags could have changed since last time, specially if the provider has tags
      }
    }

    public get anyEditing(): boolean {
      return this.editing ||
          this.channelsEditing ||
          this.categoriesEditing ||
          this.usersEditing;
    }

    public get canEdit(): boolean {
      return this.provider && !this.provider.deleted_at &&
          this.$canAction(EPermissionActions.ACTION_SERVICE_PROVIDERS_UPDATE);
    }

    public get canDelete(): boolean {
      return this.provider && !this.provider.deleted_at &&
          this.$canAction(EPermissionActions.ACTION_SERVICE_PROVIDERS_DELETE);
    }

    public loading: boolean = true;
    public editing: boolean = false;
    // viewing means we're showing details
    public viewing: boolean = false;
    public channelsEditing: boolean = false;
    public categoriesEditing: boolean = false;
    public usersEditing: boolean = false;
    public submitting: boolean = false;
    public showColorPicker: boolean = false;
    public colorPickerSwatches: any = [
      [ '#4C8BEA', '#FFD735', ],
      [ '#C3C1B9', '#37DEE9', ],
      [ '#D31C00', '#4314FF', ],
      [ '#FD2CC2', '#FF9900', ],
      [ '#01C38B', '#00FFB5', ],
    ];
    private subscribes: IOnOffHandlers = {};
    public get moduleType(): string {
      return '';
    }
    created() {
      this.resetFormData();
      this.subscribes = this.$ons({
        'login.OK': this.resetFormData,
        'backend.call.OK.serviceProviderLoaded': this.onLoaded,
        'backend.call.OK.serviceProviderDeleted': this.onDeleteOK,
        'backend.call.KO.serviceProviderDeleted': this.onDeleteKO,
      });
      if (this.isInfoCard) {
        this.fetchTags(); // Tags could have changed since last time, specially if the provider has tags
      }
    }

    beforeDestroy() {
      this.$offs(this.subscribes);
    }

    public onCancel() {
      this.resetFormData();
    }

    public get getLogoImageUrl(): string {
      return this.provider ? this.provider.logo_image_url : '';
    }

    public get isInfoCard(): boolean {
      if (this.provider) {
        return this.provider.type == EServiceProviderType.infocard || this.provider.type == EServiceProviderType.infocard_sending;
      }
      return false;
    }

    public get getPrimaryServiceDescription(): ITranslatedField {
      return this.provider ? this.provider.primary_service_description : {};
    }

    public get getPrimaryAboutUs(): ITranslatedField {
      return this.provider ? this.provider.about_us : {};
    }

    public get validateLogoImageExists(): boolean {
      return !!this.getLogoImageUrl || !!this.fileImageLogo;
    }

    public get validateAllDescriptionsAreFilled(): boolean {
      return this.getEmptyDescriptionTextSections.length === 0;
    }

    public validateProviderForm(): boolean {
      // a brand image exists if we have it already in the provider, or we have a file to upload
      // similar happens with logo image
      // all descriptions are filled if there is no value in the emptyTest translation array
      const spScope = this.$refs.spScope as any;
      const scopeValid = !spScope || spScope.isValid;

      return this.validateLogoImageExists &&
          this.validateAllDescriptionsAreFilled &&
          scopeValid;
    }
    
    public get getValidationText(): string {
      let myText: string = this.$tc('errors.serviceProvider.please');
      let possibleComma: string = '';

      if (!this.validateLogoImageExists) {
        myText = myText + possibleComma + this.$tc('errors.serviceProvider.logoImage');
        possibleComma = ', ';
      }
      
      if (!this.validateAllDescriptionsAreFilled) {
        possibleComma = possibleComma ? this.$tc('errors.serviceProvider.and') : '';
        myText = myText + possibleComma + this.$tc('errors.serviceProvider.descriptions');
      }
      
      return myText;
    }

    public onSave() {
      const spScope = this.$refs.spScope as any;
      if (spScope) {
        spScope.markSubmitted();
      }
      if (this.validateProviderForm()) {
        this.submitting = true;

        if (this.fileImageLogo) {
          this.saveImageLogoS3(this.fileImageLogo, IMAGE_ENDPOINTS.logoImage);
        }

        if (this.fileImageBrand) {
          this.saveImageLogoS3(this.fileImageBrand, IMAGE_ENDPOINTS.brandImage);
        }

        const data: IProviderForm = { ...this.provider };

        if (this.tagIds.length > 0) {
          data.tag_ids = this.tagIds.map((tag: IServiceTag | number | string) => {
            if (typeof tag === 'string') return tag as string;
            if (typeof tag === 'number') return tag as number;
            return tag.id as number;
          });
        }

        this.$store.dispatch('serviceProviderModule/updateServiceProvider', {
          serviceProviderId: this.provider.id,
          data,
        });
      } else {
        this.showValidationDialog = true;
      }
    }

    public saveImageLogo(fileToUpload: File, endpoint: IMAGE_ENDPOINTS): Promise<any> {
      return this.$store.dispatch('imageUploaderModule/uploadImage', {
        endpoint: 'serviceProviders/' + this.spId + '/' + endpoint,
        file: fileToUpload,
        translationPrefix: 'services.provider.errors',
      });
    }

    public saveImageLogoS3(fileToUpload: File, endpoint: IMAGE_ENDPOINTS): Promise<any> {
      return this.$store.dispatch('imageUploaderModule/uploadServiceProviderImage', {
        spId: this.spId,
        image: endpoint,
        file: fileToUpload,
        translationPrefix: 'services.provider.errors',
      });
    }

    public onBrandImageChange(newFile: File): Promise<any> {
      return getFileImage(newFile)
          .then((res) => {
            this.addressImageBrand = res;
            this.fileImageBrand = newFile;
          })
    }

    public onLogoImageChange(newFile: File): Promise<any> {
      return getFileImage(newFile)
          .then((res) => {
            this.addressImageLogo = res
            this.fileImageLogo = newFile;
          })
    }

    private resetFormData() {

      this.addressImageLogo = null;
      this.addressImageBrand = null;
      this.addressImageAnimation = null;

      this.fileImageLogo = null;
      this.fileImageBrand = null;
      this.fileImageAnimation = null;

      this.editing = false;
      this.channelsEditing = false;
      this.categoriesEditing = false;
      this.usersEditing = false;
      this.loading = true;
      this.$store.dispatch('serviceProviderModule/fetchServiceProvider', this.spId);
    }

    private onLoaded() {
      if (this.submitting) {
        this.submitting = false;
        this.$uiBus.emit('success', this.$t('common.saved'));
      }
      this.editing = false;
      this.loading = false;
    }

    private onCopy() {
      this.showDuplicationDialog = true;
    }

    private onCloseCopySP() {
      this.showDuplicationDialog = false;
    }

    public onDeleteProvider() {
      this.deletingProvider = true;
      this.$store.dispatch('serviceProviderModule/deleteServiceProvider', this.spId);
    }

    private onDeleteOK() {
      this.deletingProvider = false;
      this.showDeleteDialog = false;
      this.$router.push({ name: 'serviceProviderList' });
    }

    public onToggleDemo() {
      const newValue = !this.provider.is_demo;
      this.provider.is_demo = newValue;
      this.$store.dispatch('serviceProviderModule/setDemo', {
        serviceProviderId: this.provider.id,
        isDemo: newValue,
      });
    }

    private onDeleteKO(errors: any) {
      this.deletingProvider = false;
      this.showDeleteDialog = false;
      const msg = (errors && errors._ && errors._[0])
        ? errors._[0]
        : (errors && errors.error)
          ? errors.error
          : this.$t('services.provider.delete.errorOpenOrders');
      this.deleteErrorText = msg;
      this.showDeleteErrorDialog = true;
    }

    public get isProfessionalServices(): boolean {
      return this.provider.type == 'professional_services';
    }

    public get getEmptyDescriptionTextSections(): string[] {
      const psDes: string[] = this.listITranslateFieldEmptyTexts(this.getPrimaryServiceDescription);
      const aboutUs: string[] = this.listITranslateFieldEmptyTexts(this.getPrimaryAboutUs);

      const result: string[] = [...psDes, ...aboutUs];
      return [...new Set(result)]; // Remove repetitions
    }

    public listITranslateFieldEmptyTexts(textObject: ITranslatedField): string[] {
      const result: string[] = [];

      for (const property in textObject) {
        if (!textObject[property]) {
          result.push(property);
        }
      }

      return result;
    }

  }

